/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import iconShipping from '/images/icon-shipping.png';
import iconDonate from '/images/icon-donate.png';
import iconReturns from '/images/icon-returns.png';

interface ValueProp {
    id: 'shipping' | 'donate' | 'returns';
    icon: string;
    bgClassName: string;
}

const VALUE_PROPS: ValueProp[] = [
    { id: 'shipping', icon: iconShipping, bgClassName: 'bg-brand-sky' },
    { id: 'donate', icon: iconDonate, bgClassName: 'bg-brand-gold' },
    { id: 'returns', icon: iconReturns, bgClassName: 'bg-brand-lime' },
];

export default function ValuePropsBanner(): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <div className="flex w-full flex-col lg:flex-row">
            {VALUE_PROPS.map(({ id, icon, bgClassName }) => (
                <div
                    key={id}
                    className={cn(
                        'flex flex-1 flex-col items-center justify-center text-center py-10 px-6 lg:px-10',
                        bgClassName
                    )}>
                    <img
                        src={icon}
                        alt={t(`valueProps.${id}.alt`)}
                        width={64}
                        height={64}
                        className="w-16 h-16 object-contain mb-4"
                    />
                    <p className="text-sm leading-relaxed text-gray-900">
                        <strong>{t(`valueProps.${id}.boldText`)}</strong> {t(`valueProps.${id}.text`)}
                    </p>
                </div>
            ))}
        </div>
    );
}
